import React, { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import IconButton from '@mui/material/IconButton/IconButton';
import Badge from '@mui/material/Badge/Badge';
import Menu from '@mui/material/Menu/Menu';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import Typography from '@mui/material/Typography/Typography';
import Stack from '@mui/material/Stack/Stack';
import Box from '@mui/material/Box/Box';
import Button from '@mui/material/Button/Button';
import Divider from '@mui/material/Divider/Divider';
import Tooltip from '@mui/material/Tooltip/Tooltip';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import { getNotifications, markNotificationsRead, notificationsAtom } from '../../services/notification.service';

dayjs.extend(relativeTime);

const POLL_MS = 60000;

/**
 * Header bell with the signed-in user's in-app notifications (session updates and assignments).
 */
export const NotificationBell = () => {
    const { notifications, unread } = useAtomValue(notificationsAtom);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const navigate = useNavigate();

    useEffect(() => {
        const load = () => getNotifications().catch(err => console.error(err));
        load();
        const interval = window.setInterval(() => {
            if (document.visibilityState === 'visible') {
                load();
            }
        }, POLL_MS);
        window.addEventListener('focus', load);
        return () => {
            window.clearInterval(interval);
            window.removeEventListener('focus', load);
        };
    }, []);

    const close = () => setAnchorEl(null);

    return (
        <>
            <Tooltip title="Notifications">
                <IconButton
                    aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
                    onClick={(e) => setAnchorEl(e.currentTarget)}>
                    <Badge badgeContent={unread} color="secondary" max={99}>
                        <NotificationsNoneIcon />
                    </Badge>
                </IconButton>
            </Tooltip>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={close}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{ paper: { sx: { width: 360, maxWidth: 'calc(100vw - 32px)', maxHeight: 480, mt: 1 } } }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1 }}>
                    <Typography variant="subtitle1">Notifications</Typography>
                    {unread > 0 && <Button size="small" onClick={() => markNotificationsRead()}>Mark all read</Button>}
                </Stack>
                <Divider />
                {
                    notifications.length === 0 && (
                        <Box sx={{ px: 2, py: 3 }}>
                            <Typography variant="body2" color="text.secondary">You're all caught up.</Typography>
                        </Box>
                    )
                }
                {
                    notifications.map(n => (
                        <MenuItem
                            key={n._id}
                            onClick={() => {
                                close();
                                if (!n.readAt) {
                                    markNotificationsRead([n._id]).catch(err => console.error(err));
                                }
                                if (n.link) {
                                    navigate(n.link);
                                }
                            }}
                            sx={{ alignItems: 'flex-start', whiteSpace: 'normal', gap: 1.5, py: 1.25 }}>
                            <Box sx={{
                                width: 8,
                                height: 8,
                                mt: 0.9,
                                flexShrink: 0,
                                borderRadius: '50%',
                                backgroundColor: n.readAt ? 'transparent' : 'secondary.main'
                            }} />
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: n.readAt ? 400 : 600 }}>{n.title}</Typography>
                                {n.body && <Typography variant="body2" color="text.secondary">{n.body}</Typography>}
                                <Typography variant="caption" color="text.secondary">{dayjs(n.createdAt).fromNow()}</Typography>
                            </Box>
                        </MenuItem>
                    ))
                }
            </Menu>
        </>
    )
}
